import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

function formatoPesos(value) {
    return '$ ' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function FormatoFechaSinHora(value) {
    const fecha = new Date(value);
    return format(fecha, 'dd-MMMM-yyyy', { locale: es }).toUpperCase();
}

function FormatoFechaConHora(value) {
    const fecha = new Date(value);
    return format(fecha, "dd-MMMM-yyyy HH:mm:ss", { locale: es }).toUpperCase();
}

function calcularAnchoProporcional(base64, alturaFija) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const ancho = img.naturalWidth && img.naturalHeight
                ? (img.naturalWidth * alturaFija) / img.naturalHeight
                : 50;
            resolve(ancho);
        };
        img.onerror = () => resolve(50);
        img.src = base64;
    });
}

function detectarFormatoImagen(base64) {
    if (base64.startsWith('data:image/png'))  return 'PNG';
    if (base64.startsWith('data:image/jpeg')) return 'JPEG';
    if (base64.startsWith('data:image/jpg'))  return 'JPEG';
    if (base64.startsWith('data:image/gif'))  return 'GIF';
    if (base64.startsWith('data:image/webp')) return 'WEBP';
    return 'PNG';
}

const MayoresPdf = async (objMovimientos, objEmpresa, objColor, objAño) => {
    return new Promise(async (resolve, reject) => {
        try {
            const doc = new jsPDF({ format: 'letter' });

            const color               = objColor || objEmpresa?.color || '#662e91';
            const logoBase64          = objEmpresa?.logo?.base64 || '';
            const fechaHoraGeneracion = FormatoFechaConHora(new Date());

            const margenIzq = 12;
            const margenDer = 12;
            const anchoUtil = 215 - margenIzq - margenDer; // ~191mm
            const xDer      = 215 - margenDer;

            // ── CABECERA ──────────────────────────────────────────────────
            let y = 14;

            if (logoBase64 !== '') {
                const alturaLogo = 20;
                const formatoImg = detectarFormatoImagen(logoBase64);
                const anchoLogo  = await calcularAnchoProporcional(logoBase64, alturaLogo);
                doc.addImage(logoBase64, formatoImg, margenIzq, y, anchoLogo, alturaLogo);
            }

            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text('Reporte de Mayores', xDer, y, { align: 'right' });

            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text((objEmpresa?.rfc || '') + ' | ' + (objEmpresa?.nombre || ''), xDer, y += 6, { align: 'right' });
            doc.text('PERIODO ' + objAño, xDer, y += 5, { align: 'right' });

            y += 10;

            // ── TABLA DE MOVIMIENTOS POR CUENTA ──────────────────────────
            // Construimos un solo body agrupando cabecera de cuenta + filas de detalle
            const body = [];

            for (let m of objMovimientos) {
                // Fila cabecera de cuenta
                body.push([
                    {
                        content: (m.numeroCuenta || '') + ' | ' + (m.nombrecuenta || ''),
                        colSpan: 7,
                        styles: {
                            fontStyle: 'bold',
                            fillColor: '#ede7f6',
                            textColor: color,
                            halign: 'left',
                        }
                    }
                ]);

                // Filas de detalle por mes
                for (let d of m.detalles) {
                    body.push([
                        { content: d.mes || '',                         styles: { halign: 'left'  } },
                        { content: formatoPesos(d.saldoInicial),        styles: { halign: 'right' } },
                        { content: formatoPesos(d.cargos),              styles: { halign: 'right' } },
                        { content: formatoPesos(d.abonos),              styles: { halign: 'right' } },
                        { content: formatoPesos(d.saldoFinal),          styles: { halign: 'right' } },
                        { content: formatoPesos(d.acumuladosCargos),    styles: { halign: 'right' } },
                        { content: formatoPesos(d.acumuladosAbonos),    styles: { halign: 'right' } },
                    ]);
                }
            }

            doc.autoTable({
                startY: y,
                head: [[
                    { content: 'Mes',                styles: { halign: 'left'  } },
                    { content: 'Saldo Inicial',      styles: { halign: 'right' } },
                    { content: 'Cargos',             styles: { halign: 'right' } },
                    { content: 'Abonos',             styles: { halign: 'right' } },
                    { content: 'Saldo Final',        styles: { halign: 'right' } },
                    { content: 'Acum. Cargos',       styles: { halign: 'right' } },
                    { content: 'Acum. Abonos',       styles: { halign: 'right' } },
                ]],
                body,
                styles: {
                    fontSize: 6,
                    cellPadding: 1,
                    overflow: 'linebreak',
                },
                headStyles: {
                    fillColor: color,
                    textColor: '#ffffff',
                    fontStyle: 'bold',
                },
                alternateRowStyles: { fillColor: '#f4f4f4' },
                columnStyles: {
                    0: { cellWidth: 24 },   // Mes
                    1: { cellWidth: 27 },   // Saldo Inicial
                    2: { cellWidth: 27 },   // Cargos
                    3: { cellWidth: 27 },   // Abonos
                    4: { cellWidth: 27 },   // Saldo Final
                    5: { cellWidth: 27 },   // Acum. Cargos
                    6: { cellWidth: 32 },   // Acum. Abonos
                },
                margin: { left: margenIzq, right: margenDer },
                tableWidth: anchoUtil,
                // Evitar que la fila de cabecera de cuenta quede sola al final de página
                rowPageBreak: 'avoid',
            });

            // ── PIE DE PÁGINA ─────────────────────────────────────────────
            const totalPages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(8);
                doc.setTextColor(150);

                const pageH = doc.internal.pageSize.getHeight();
                const pageW = doc.internal.pageSize.getWidth();

                doc.setDrawColor(color);
                doc.setLineWidth(0.3);
                doc.line(margenIzq, pageH - 16, pageW - margenDer, pageH - 16);

                doc.text('Generado: ' + fechaHoraGeneracion, margenIzq, pageH - 8, { align: 'left' });
                doc.text('Documento generado por ContaGo', pageW / 2, pageH - 8, { align: 'center' });
                doc.text('Página ' + i + ' de ' + totalPages, pageW - margenDer, pageH - 8, { align: 'right' });
            }

            const base64 = doc.output('datauristring');
            resolve(base64);
        } catch (error) {
            console.error(error);
            reject(error);
        }
    });
};

export { MayoresPdf };